import React from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import usePath from 'utils/hooks/use-path';
import useQuery from 'utils/hooks/use-query';
import {
  PageNumber,
  Next,
  Previous,
  GapIndicator,
  PageNumbers,
  MobilePageNumbersLabel
} from './styles';

const IDEAL_NUMBER_OF_PAGES = 6;
const MAX_FLANKING_PAGES = 4;
const MIN_FLANKING_PAGES = 2;
const MAX_CURRENT_PAGE_FLANKS = 2;

const Pager = ({ currentPage, totalPages }) => {
  currentPage = Math.abs(currentPage) || 1;
  const query = useQuery(['page']).asObject;
  const path = usePath();
  if (currentPage > totalPages) {
    return null;
  }

  const pages = getPageNumbers(currentPage, totalPages);

  const previousLink =
    currentPage > 1
      ? `${path}?${querystring.stringify({
          ...query,
          page: currentPage - 1
        })}`
      : null;

  const nextLink =
    currentPage < totalPages
      ? `${path}?${querystring.stringify({
          ...query,
          page: currentPage + 1
        })}`
      : null;

  return (
    <>
      {previousLink ? (
        <Link href={previousLink}>
          <Previous active={currentPage > 1} />
        </Link>
      ) : (
        <Previous active={currentPage > 1} />
      )}
      <PageNumbers>
        {pages.map((p, index) => (
          <Link
            href={`${path}?${querystring.stringify({ ...query, page: p })}`}
            key={index}
          >
            <PageNumber active={p === currentPage}>{p}</PageNumber>
          </Link>
        ))}
      </PageNumbers>
      <MobilePageNumbersLabel>
        Page &nbsp;{currentPage}&nbsp; of &nbsp;{totalPages}
      </MobilePageNumbersLabel>
      {nextLink ? (
        <Link href={nextLink}>
          <Next active={currentPage < totalPages} />
        </Link>
      ) : (
        <Next active={currentPage < totalPages} />
      )}
    </>
  );
};

Pager.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number
};

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= IDEAL_NUMBER_OF_PAGES) {
    return getPageNumbersWhenTotalPagesIsIdealOrLess(currentPage, totalPages);
  }
  if (currentPage <= MAX_FLANKING_PAGES) {
    return getPageNumbersWhenCurrentPageIsCloseToStart(currentPage, totalPages);
  }

  if (currentPage > totalPages - MAX_FLANKING_PAGES) {
    return getPageNumbersWhenCurrentPageIsCloseToEnd(currentPage, totalPages);
  }

  return getPageNumbersWhenCurrentPageIsInMiddle(currentPage, totalPages);
}

function getPageNumbersWhenTotalPagesIsIdealOrLess(currentPage, totalPages) {
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
  }

  return pages;
}

function getPageNumbersWhenCurrentPageIsCloseToStart(currentPage, totalPages) {
  const pages = [];

  const firstPageGroup = [];
  for (let i = 1; i <= MAX_FLANKING_PAGES; i++) {
    firstPageGroup.push(i);
  }

  if (MAX_FLANKING_PAGES - currentPage < MAX_CURRENT_PAGE_FLANKS) {
    for (
      let i = currentPage + 1;
      i <= currentPage + MAX_CURRENT_PAGE_FLANKS;
      i++
    ) {
      if (!firstPageGroup.includes(i)) firstPageGroup.push(i);
    }
  }

  const secondPageGroup = [];
  for (let i = totalPages; i > totalPages - MIN_FLANKING_PAGES; i--) {
    secondPageGroup.unshift(i);
  }

  const gapExistsBetweenPageGroups =
    secondPageGroup[0] - firstPageGroup[firstPageGroup.length - 1] > 1;

  pages.push(
    ...firstPageGroup,
    gapExistsBetweenPageGroups ? <GapIndicator /> : MAX_FLANKING_PAGES + 1,
    ...secondPageGroup
  );

  return pages;
}

function getPageNumbersWhenCurrentPageIsInMiddle(currentPage, totalPages) {
  const pages = [];

  const leftPageGroup = [];
  for (let i = 1; i <= MIN_FLANKING_PAGES; i++) {
    leftPageGroup.push(i);
  }

  const rightPageGroup = [];
  for (let i = totalPages; i > totalPages - MIN_FLANKING_PAGES; i--) {
    rightPageGroup.unshift(i);
  }

  const middlePageGroup = [];
  for (
    let i = currentPage - MAX_CURRENT_PAGE_FLANKS;
    i <= currentPage + MAX_CURRENT_PAGE_FLANKS;
    i++
  ) {
    middlePageGroup.push(i);
  }

  const gapExistsOnLeft =
    middlePageGroup[0] - leftPageGroup[leftPageGroup.length - 1] > 1;

  const gapExistsOnRight =
    rightPageGroup[0] - middlePageGroup[middlePageGroup.length - 1] > 1;

  if (gapExistsOnLeft) leftPageGroup.push(<GapIndicator />);
  if (gapExistsOnRight) rightPageGroup.unshift(<GapIndicator />);

  pages.push(...leftPageGroup, ...middlePageGroup, ...rightPageGroup);
  return pages;
}

function getPageNumbersWhenCurrentPageIsCloseToEnd(currentPage, totalPages) {
  const pages = [];
  const firstPageGroup = [];
  for (let i = 1; i <= MIN_FLANKING_PAGES; i++) {
    firstPageGroup.push(i);
  }

  const numberOfPagesLeftFlankingCurrent =
    MAX_CURRENT_PAGE_FLANKS - (totalPages - currentPage - 1);

  const secondPageGroup = [];
  for (let i = totalPages; i > totalPages - MAX_FLANKING_PAGES; i--) {
    secondPageGroup.unshift(i);
  }

  if (numberOfPagesLeftFlankingCurrent < MAX_FLANKING_PAGES) {
    for (
      let i = currentPage - 1;
      i >= currentPage - MAX_CURRENT_PAGE_FLANKS;
      i--
    ) {
      if (!secondPageGroup.includes(i)) secondPageGroup.unshift(i);
    }
  }

  const gapExistsBetweenPageGroups =
    secondPageGroup[0] - firstPageGroup[firstPageGroup.length - 1] > 1;

  pages.push(
    ...firstPageGroup,
    gapExistsBetweenPageGroups ? <GapIndicator /> : MIN_FLANKING_PAGES + 1,
    ...secondPageGroup
  );

  return pages;
}

export default React.memo(Pager);
